import has from 'lodash/has';
import {
  cabinBaggage,
  checkInBaggage,
  flexibleTicket,
  meal,
  secondBaggagePerTraveler,
  seatMap,
  withParseAvailability,
} from '@eti/products';

const travelerProducts = [
  cabinBaggage.id,
  checkInBaggage.id,
  flexibleTicket.id,
  meal.id,
  secondBaggagePerTraveler.id,
  seatMap.id,
];

export const hasAvailability = (productSpecificTexts) =>
  Boolean(productSpecificTexts?.productSegmentAvailabilityTexts);

export const getNumberOfUnits = (buySpecification) => {
  if (has(buySpecification, 'travelerPrices')) {
    return buySpecification.travelerPrices.length;
  }

  if (has(buySpecification, 'travelerChoicePrices')) {
    return buySpecification.travelerChoicePrices.length;
  }

  if (has(buySpecification, 'travelerBaggageTripPrices')) {
    return buySpecification.travelerBaggageTripPrices.filter(
      (el, i, arr) => arr.findIndex((obj) => obj.travelerId === el.travelerId) === i,
    ).length;
  }

  if (has(buySpecification, 'travelerSeatMapPrices')) {
    return buySpecification.travelerSeatMapPrices.length;
  }

  return null;
};

export const getTravelers = (buySpecification, travelers, productId) => {
  if (travelerProducts.includes(productId)) {
    if (has(buySpecification, 'travelerPrices')) {
      return buySpecification.travelerPrices;
    }

    if (has(buySpecification, 'travelerChoicePrices')) {
      return buySpecification.travelerChoicePrices;
    }

    if (has(buySpecification, 'travelerSeatMapDetails')) {
      return travelers.map((traveler) => {
        const seatDetails =
          (buySpecification.travelerSeatMapDetails || []).filter(
            (seatMapTraveler) => String(seatMapTraveler.travelerId) === String(traveler.id),
          ) || [];

        if (seatDetails.length > 0) {
          const { priceRaw, markup } = seatDetails.reduce(
            (total, { price }) => ({
              priceRaw: total.priceRaw + (price?.price?.value || 0),
              markup: total.markup + (price?.markup?.value || 0),
            }),
            { priceRaw: 0, markup: 0 },
          );

          return {
            ancillaryCarrierPriceBreakdowns: seatDetails[0]?.ancillaryCarrierPriceBreakdowns ?? [],
            travelerId: traveler.id,
            priceRaw,
            markup,
          };
        }

        return [];
      });
    }

    if (has(buySpecification, 'travelerCabinBaggagePrices')) {
      return travelers.map((traveler) => {
        const baggageDetails = buySpecification.travelerCabinBaggagePrices.filter(
          (product) => product.travelerId === traveler.id,
        );

        if (baggageDetails.length > 0) {
          return {
            ancillaryCarrierPriceBreakdowns: baggageDetails[0].ancillaryCarrierPriceBreakdowns,
            travelerId: traveler.id,
            priceRaw: baggageDetails.reduce((total, { price }) => total + price.price?.value, 0),
            markup: baggageDetails.reduce((total, { price }) => total + price.markup?.value, 0),
          };
        }
        return [];
      });
    }

    if (has(buySpecification, 'travelerBaggageTripPrices')) {
      return travelers.map((traveler) => {
        const baggageDetails = buySpecification.travelerBaggageTripPrices.filter(
          (product) => product.travelerId === traveler.id,
        );

        if (baggageDetails.length > 0) {
          return {
            ancillaryCarrierPriceBreakdowns: baggageDetails[0].ancillaryCarrierPriceBreakdowns,
            travelerId: traveler.id,
            priceRaw: baggageDetails.reduce((total, { price }) => total + price.price?.value, 0),
            markup: baggageDetails.reduce((total, { price }) => total + price.markup?.value, 0),
            pieces: baggageDetails[0].pieces,
            maxWeight: baggageDetails[0].maxWeight,
            weightUnit: baggageDetails[0].weightUnit,
          };
        }
        return [];
      });
    }
  }
  return [];
};

export const transformProducts = (getTranslation, products = [], travelers = [], bounds = []) => {
  return products.map(
    ({
      productId,
      name,
      price,
      buySpecification,
      text,
      productSummaryAlternativeName,
      code,
      productSpecificTexts,
      ...rest
    }) => {
      const numberOfUnits = getNumberOfUnits(buySpecification);
      const transformedTravelers = getTravelers(buySpecification, travelers, Number(productId));
      const parseAvailability = withParseAvailability(
        Number(productId),
        getTranslation,
        `${name.toLowerCase()}GeneralPurpose`,
      );

      return {
        id: productId,
        code,
        title: productSummaryAlternativeName || name,
        price: price.price.value,
        markup: price.markup && price.markup.value,
        numberOfUnits,
        text,
        travelers: transformedTravelers,
        availability: hasAvailability(productSpecificTexts)
          ? parseAvailability(
              productSpecificTexts?.productSegmentAvailabilityTexts,
              bounds,
              travelers.length,
            )
          : null,
        ...rest,
      };
    },
  );
};

export const transformPaymentLinkProducts = (products = []) => {
  return products.map(
    ({
      code,
      productId,
      name,
      price,
      buySpecification,
      text,
      travelers,
      productSummaryAlternativeName,
    }) => ({
      code,
      id: productId,
      title: productSummaryAlternativeName || name,
      price: price.price.value,
      markup: price.markup && price.markup.value,
      numberOfUnits: getNumberOfUnits(buySpecification),
      text,
      travelers,
    }),
  );
};
